import { useRef, useState } from "react";
import { Camera, RotateCcw, Image as ImageIcon, Loader2 } from "lucide-react";

/**
 * Compresses an image File into a JPEG data URL, resized to fit within maxDim.
 * Returns { dataUrl, sizeKB } via async callback.
 */
async function compressImage(file, { maxDim = 1280, quality = 0.82 } = {}) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  const ratio = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  const out = canvas.toDataURL("image/jpeg", quality);
  const sizeKB = Math.round((out.length * 0.75) / 1024);
  return { dataUrl: out, sizeKB };
}

/**
 * Photo capture with file input + camera support.
 * Compresses image (max 1280px, JPEG 82%) before storing base64.
 */
export default function PhotoCapture({ label, value, onChange, testid }) {
  const fileRef = useRef(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [info, setInfo] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("File harus berupa gambar"); return; }
    if (file.size > 15 * 1024 * 1024) { setError("Ukuran foto maksimal 15MB"); return; }
    setError("");
    setProcessing(true);
    try {
      const { dataUrl, sizeKB } = await compressImage(file);
      onChange?.(dataUrl);
      setInfo(`${sizeKB} KB (dikompres)`);
    } catch (e) {
      setError("Gagal memproses foto");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid={testid}>
      <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-brand-soft border border-brand/15 flex items-center justify-center relative">
        {value ? (
          <img src={value} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="text-center text-brand-muted">
            <ImageIcon className="mx-auto mb-2" />
            <div className="text-sm">Belum ada foto</div>
          </div>
        )}
        {processing && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-2 text-brand">
            <Loader2 className="animate-spin" />
            <div className="text-xs">Memproses foto…</div>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={processing}
          onClick={() => fileRef.current?.click()}
          data-testid={(testid || "photo") + "-capture-btn"}
          className="rounded-full bg-brand text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-brand-hover transition disabled:opacity-60"
        >
          <Camera size={14} /> {value ? "Ambil Ulang" : "Ambil / Unggah Foto"}
        </button>
        {value && !processing && (
          <button
            type="button"
            onClick={() => { onChange?.(null); setInfo(""); }}
            data-testid={(testid || "photo") + "-clear-btn"}
            className="rounded-full border border-amber-300 text-amber-700 px-4 py-2 text-sm flex items-center gap-2"
          >
            <RotateCcw size={14} /> Hapus
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={(testid || "photo") + "-file-input"}
        />
      </div>
      {info && !error && <div className="mt-2 text-xs text-brand-muted">{info}</div>}
      {error && <div className="mt-2 text-xs text-red-500">{error}</div>}
    </div>
  );
}
